import { useParams, useNavigate } from "react-router-dom";
import styles from "./OrderDetails.module.css";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import Spin from "../Spinner/Spinner";
import { FiUser, FiCalendar, FiImage, FiDownload } from "react-icons/fi";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

const formatOrderId = (order) => {
  if (!order?._id) return "-";
  const year = order.createdAt
    ? new Date(order.createdAt).getFullYear()
    : new Date().getFullYear();
  const shortId = order._id.slice(-4).toUpperCase();
  return `ORD-${year}-${shortId}`;
};

const OrderRow = ({ order }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const productImages = order?.productImages ?? [];
  const mockupImages = order?.mockupImages ?? [];
  const totalImages = productImages.length + mockupImages.length;

  const userName = order?.user?.name || order?.user?.companyName || "-";

  const handleDownloadImage = async (url) => {
    try {
      if (!url) return;
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentType = blob.type || "";
      const rawName = url.split("/").pop() || "image";
      let extension = "";
      if (contentType.includes("png")) extension = ".png";
      else if (contentType.includes("jpeg") || contentType.includes("jpg"))
        extension = ".jpg";
      else if (contentType.includes("webp")) extension = ".webp";
      const hasExtension = /\.[a-zA-Z0-9]+$/.test(rawName);
      const fileName = hasExtension
        ? rawName
        : `${rawName}${extension || ".png"}`;
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download image", error);
    }
  };

  return (
    <div className={styles.orderRow}>
      <button
        type="button"
        className={styles.orderRowHeader}
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className={styles.orderMainInfo}>
          <div className={styles.orderTitle}>{order?.user?.companyName || order?.user?.name || "-"}</div>
          <div className={styles.orderSubInfo}>
            <span>
              <FiUser className={styles.orderSubIcon} />
              {userName}
            </span>
            <span>
              <FiCalendar className={styles.orderSubIcon} />
              {formatDate(order?.createdAt)}
            </span>
          </div>
        </div>
        <div className={styles.orderHeaderRight}>
          <span className={styles.orderImagesCount}>
            <FiImage className={styles.orderCountIcon} />
            {totalImages} image{totalImages !== 1 ? "s" : ""}
          </span>
          <span className={styles.orderChevron}>{open ? "▲" : "▼"}</span>
        </div>
      </button>
      <div className={styles.orderImagesContainer}>
        {open && (
          <div className={styles.orderImagesSection}>
            <button
              type="button"
              className={styles.viewOrderButton}
              onClick={() => navigate(`/orders/${order._id}`)}
            >
              View Order
            </button>
            <div className={styles.orderImagesRow}>
              <div className={styles.orderImagesGroupWrap}>
                {productImages.length > 0 && (
                  <div className={styles.orderImagesGroup}>
                    <div className={styles.orderImagesGroupLabel}>
                      Product Images
                    </div>
                    <div className={styles.orderImagesGroupCount}>
                      {productImages.length} image
                      {productImages.length !== 1 ? "s" : ""}
                    </div>
                    <div className={styles.orderImagesGrid}>
                      {productImages.map((img, idx) => (
                        <div key={idx} className={styles.orderImageWrapper}>
                          <img
                            src={img}
                            alt=""
                            className={styles.orderImageThumb}
                          />
                          <div
                            className={styles.orderImageDownload}
                            onClick={() => handleDownloadImage(img)}
                            aria-label="Download image"
                          >
                            <FiDownload />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.orderImagesGroupWrap}>
                {mockupImages.length > 0 && (
                  <div className={styles.orderImagesGroup}>
                    <div className={styles.orderImagesGroupLabel}>
                      Mockup Images
                    </div>
                    <div className={styles.orderImagesGroupCount}>
                      {mockupImages.length} image
                      {mockupImages.length !== 1 ? "s" : ""}
                    </div>
                    <div className={styles.orderImagesGrid}>
                      {mockupImages.map((img, idx) => (
                        <div key={idx} className={styles.orderImageWrapper}>
                          <img
                            src={img}
                            alt=""
                            className={styles.orderImageThumb}
                          />
                          <div
                            className={styles.orderImageDownload}
                            onClick={() => handleDownloadImage(img)}
                            aria-label="Download image"
                          >
                            <FiDownload />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ProductOrderDetails = () => {
  const { id } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productName, setProductName] = useState("");

  const getProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/Admin/cartOrders/products/${id}`,
      );
      const data = response.data || {};
      setOrders(Array.isArray(data.orders) ? data.orders : []);
      setProductName(data.productName || "Product");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProduct();
  }, [id]);

  if (loading) {
    return <Spin />;
  }

  return (
    <div className={styles.container}>
      <h1>Product Order Details</h1>
      {productName && <p className={styles.subheading}>{productName}</p>}
      <div className={styles.ordersListContainer}>
        <div className={styles.ordersListHeader}>
          <h4>Orders Containing {orders?.[0]?.productName}</h4>
          <p>Total Orders: {orders.length}</p>
        </div>
        {orders.length === 0 ? (
          <p className={styles.noOrders}>No orders found</p>
        ) : (
          orders.map((order) => <OrderRow key={order._id} order={order} />)
        )}
      </div>
    </div>
  );
};
